import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { TooltipTrigger, type TooltipPlacement } from '../Tooltip/TooltipTrigger';
import './ReadOnly.css';

export type ReadOnlyState = {
  readOnly: boolean;
  /** Why actions are disabled, shown as their tooltip (e.g. "Paused during the update"). */
  reason?: string;
};

const ReadOnlyContext = createContext<ReadOnlyState>({ readOnly: false });

export type ReadOnlyProviderProps = ReadOnlyState & { children?: ReactNode };

/**
 * Puts everything inside it in read-only mode: browsing keeps working, while
 * actions that opt in — `Button` with `disableWhenReadOnly`, or anything wrapped
 * in `ReadOnlyGuard` — are disabled and explain why on hover.
 */
export function ReadOnlyProvider({ readOnly, reason, children }: ReadOnlyProviderProps) {
  const value = useMemo(() => ({ readOnly, reason }), [readOnly, reason]);

  useEffect(() => {
    if (!readOnly) return undefined;
    // Uploads are paused: a file dropped anywhere must neither upload nor make the browser open it.
    const blockFileDrop = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', blockFileDrop, true);
    window.addEventListener('drop', blockFileDrop, true);
    return () => {
      window.removeEventListener('dragover', blockFileDrop, true);
      window.removeEventListener('drop', blockFileDrop, true);
    };
  }, [readOnly]);

  return <ReadOnlyContext.Provider value={value}>{children}</ReadOnlyContext.Provider>;
}

export function useReadOnly(): ReadOnlyState {
  return useContext(ReadOnlyContext);
}

/** For Spark components with a `disableWhenReadOnly` prop: whether that prop pauses them right now, and why. */
export function useReadOnlyPause(disableWhenReadOnly: boolean | undefined): { paused: boolean; reason?: string } {
  const { readOnly, reason } = useReadOnly();
  return { paused: Boolean(disableWhenReadOnly) && readOnly, reason };
}

type ReadOnlyTooltipProps = {
  paused: boolean;
  reason?: string;
  /** For block rows such as menu items, so the trigger spans the row. */
  block?: boolean;
  placement?: TooltipPlacement;
  children: ReactNode;
};

/** Shows the read-only reason on hover over a paused control; renders the control alone otherwise. */
export function ReadOnlyTooltip({ paused, reason, block = false, placement = 'bottom', children }: ReadOnlyTooltipProps) {
  if (!paused || !reason) return <>{children}</>;
  return (
    <TooltipTrigger
      text={reason}
      placement={placement}
      className={['spark-read-only-tooltip', block ? 'spark-read-only-tooltip-block' : ''].filter(Boolean).join(' ')}
    >
      {children}
    </TooltipTrigger>
  );
}

export type ReadOnlyGuardProps = {
  children: ReactNode;
  placement?: TooltipPlacement;
  /** For full-width content (drop zones, editors, full-width buttons), so it keeps its width while paused. */
  block?: boolean;
  className?: string;
};

/**
 * Disables whatever it wraps while read-only, with the reason as a tooltip. For
 * actions that are not a Spark `Button` (menus, legacy buttons, upload areas).
 */
export function ReadOnlyGuard({ children, placement = 'bottom', block = false, className = '' }: ReadOnlyGuardProps) {
  const { readOnly, reason } = useReadOnly();
  if (!readOnly) return <>{children}</>;
  return (
    <TooltipTrigger
      text={reason ?? ''}
      disabled={!reason}
      placement={placement}
      className={['spark-read-only-guard', block ? 'spark-read-only-guard--block' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {/* inert: no clicks, focus or keyboard inside, and hover lands on the tooltip trigger. */}
      <span className="spark-read-only-guard-content" aria-disabled="true" {...{ inert: '' }}>
        {children}
      </span>
    </TooltipTrigger>
  );
}
